import type { ToolCallEntry } from "./useTurns";

// The pydantic-ai harness `Planning` capability (enable_subtasks=True) registers
// these tools. They stream over AG-UI as ordinary tool calls and persist +
// replay like any other, so a turn's checklist is derived purely on the client
// by folding them in order. `add_subtask` nests a step under a parent;
// `set_dependency` / `get_available_tasks` only drive server-side dependency
// reconciliation, whose effect (a `blocked` status) surfaces on the next
// `read_plan` — so the fold recognizes them (keeping them out of the transcript)
// without mutating the list itself. See `_planning_capabilities`.
export const PLAN_TOOL_NAMES = [
  "write_plan",
  "read_plan",
  "add_task",
  "add_subtask",
  "set_dependency",
  "get_available_tasks",
  "update_task_status",
  "update_task_statuses",
  "remove_task",
] as const;

const PLAN_TOOL_NAME_SET: ReadonlySet<string> = new Set(PLAN_TOOL_NAMES);

export const isPlanTool = (name: string): boolean => PLAN_TOOL_NAME_SET.has(name);

export const PlanTaskStatus = {
  pending: "pending",
  in_progress: "in_progress",
  completed: "completed",
  cancelled: "cancelled",
  blocked: "blocked",
} as const;
export type PlanTaskStatus = (typeof PlanTaskStatus)[keyof typeof PlanTaskStatus];

export interface PlanTask {
  // Stable React key and update target: the harness step id when the model
  // authored one, otherwise a positional fallback derived from the tool-call id
  // (`#<toolCallId>[:<index>]`) so a plan written without ids still renders and
  // re-renders stably. An id-keyed update can only match a real harness id, so a
  // positional task is simply left untouched by later granular edits.
  id: string;
  content: string;
  status: PlanTaskStatus;
  // Parent step id when the model nested this step under another (via
  // `add_subtask`, or a `write_plan` item that set `parent_id`). Drives indented
  // rendering; a task whose parent is absent renders as a root.
  parentId?: string;
  // Present-continuous label the model may attach to a step ("Profiling the
  // upload"); shown in place of `content` while the step is in progress.
  activeForm?: string;
}

// Folds a turn's ordered plan-tool calls into the current task list. Non-plan
// calls are skipped, so callers can pass a whole turn's `toolCalls`.
//
// Args-first: `write_plan`/`add_task`/`update_task_status[es]`/`remove_task`
// carry their full intent in the model-authored args (present even before the
// tool result arrives), and `write_plan` is a whole-plan replacement. Results
// are consulted only where they add information the args lack: `add_task`'s
// generated step id, and `read_plan`'s id-bearing snapshot (the one tool whose
// result string carries ids). Every parse is tolerant — a malformed or partial
// call leaves the plan unchanged rather than throwing.
export function derivePlan(entries: readonly ToolCallEntry[]): PlanTask[] {
  let tasks: PlanTask[] = [];
  for (const entry of entries) {
    if (!isPlanTool(entry.name)) continue;
    tasks = applyPlanCall(tasks, entry);
  }
  return tasks;
}

function applyPlanCall(tasks: PlanTask[], entry: ToolCallEntry): PlanTask[] {
  switch (entry.name) {
    case "write_plan":
      return parseWritePlan(entry) ?? tasks;
    case "read_plan":
      return parseReadPlanResult(entry.result) ?? tasks;
    case "add_task":
      return applyAddTask(tasks, entry);
    case "add_subtask":
      return applyAddSubtask(tasks, entry);
    case "update_task_status":
      return applyStatusUpdate(tasks, entry);
    case "update_task_statuses":
      return applyStatusUpdates(tasks, entry);
    case "remove_task":
      return applyRemoveTask(tasks, entry);
    // `set_dependency` records a prerequisite (the server may auto-`blocked` the
    // dependent, which shows up on the next `read_plan`) and `get_available_tasks`
    // is read-only: neither adds or renames a step, so fold them without changing
    // the list — recognizing them here keeps them out of the visible transcript.
    case "set_dependency":
    case "get_available_tasks":
      return tasks;
    default:
      return tasks;
  }
}

interface RawPlanItem {
  id?: unknown;
  content?: unknown;
  status?: unknown;
  active_form?: unknown;
  parent_id?: unknown;
}

// `write_plan` replaces the whole plan; its args are `{ items: [...] }` with one
// entry per step. Returns null (keep the prior plan) if the args are unparseable
// or lack an items array — e.g. while the call is still streaming.
function parseWritePlan(entry: ToolCallEntry): PlanTask[] | null {
  const parsed = parseJson(entry.args);
  const items = isRecord(parsed) ? parsed.items : undefined;
  if (!Array.isArray(items)) return null;
  return items.map((raw, index) => toTask(raw, `#${entry.id}:${index}`));
}

function applyAddTask(tasks: PlanTask[], entry: ToolCallEntry): PlanTask[] {
  const parsed = parseJson(entry.args);
  if (!isRecord(parsed) || typeof parsed.content !== "string") return tasks;
  const id = parseAddedId(entry.result) ?? `#${entry.id}`;
  const task: PlanTask = {
    id,
    content: parsed.content,
    status: PlanTaskStatus.pending,
    ...(typeof parsed.active_form === "string" && parsed.active_form
      ? { activeForm: parsed.active_form }
      : {}),
  };
  return [...tasks, task];
}

// `add_subtask` appends a pending child under an existing step. Its args carry
// `{ parent_id, content }`; its result — `Added subtask '<c>' with id: <id>
// (parent: <p>)` — carries the generated id. Parent comes from the args so the
// row renders indented immediately; the id falls back to a positional key until
// the result resolves the real one (an id-keyed update then matches only after,
// mirroring `add_task`).
function applyAddSubtask(tasks: PlanTask[], entry: ToolCallEntry): PlanTask[] {
  const parsed = parseJson(entry.args);
  if (!isRecord(parsed) || typeof parsed.content !== "string") return tasks;
  const id = parseAddedId(entry.result) ?? `#${entry.id}`;
  const task: PlanTask = {
    id,
    content: parsed.content,
    status: PlanTaskStatus.pending,
    ...(typeof parsed.parent_id === "string" && parsed.parent_id
      ? { parentId: parsed.parent_id }
      : {}),
    ...(typeof parsed.active_form === "string" && parsed.active_form
      ? { activeForm: parsed.active_form }
      : {}),
  };
  return [...tasks, task];
}

function applyStatusUpdate(tasks: PlanTask[], entry: ToolCallEntry): PlanTask[] {
  const parsed = parseJson(entry.args);
  if (!isRecord(parsed)) return tasks;
  const status = normalizeStatus(parsed.status);
  if (typeof parsed.task_id !== "string" || status === null) return tasks;
  return setStatus(tasks, parsed.task_id, status);
}

function applyStatusUpdates(tasks: PlanTask[], entry: ToolCallEntry): PlanTask[] {
  const parsed = parseJson(entry.args);
  const updates = isRecord(parsed) ? parsed.updates : undefined;
  if (!Array.isArray(updates)) return tasks;
  let next = tasks;
  for (const update of updates) {
    if (!isRecord(update)) continue;
    const status = normalizeStatus(update.status);
    if (typeof update.task_id !== "string" || status === null) continue;
    next = setStatus(next, update.task_id, status);
  }
  return next;
}

function applyRemoveTask(tasks: PlanTask[], entry: ToolCallEntry): PlanTask[] {
  const parsed = parseJson(entry.args);
  if (!isRecord(parsed) || typeof parsed.task_id !== "string") return tasks;
  return tasks.filter((task) => task.id !== parsed.task_id);
}

function setStatus(tasks: PlanTask[], id: string, status: PlanTaskStatus): PlanTask[] {
  return tasks.map((task) => (task.id === id ? { ...task, status } : task));
}

function toTask(raw: unknown, fallbackId: string): PlanTask {
  const item = isRecord(raw) ? (raw as RawPlanItem) : {};
  const id = typeof item.id === "string" && item.id ? item.id : fallbackId;
  const content = typeof item.content === "string" ? item.content : "";
  return {
    id,
    content,
    status: normalizeStatus(item.status) ?? PlanTaskStatus.pending,
    ...(typeof item.parent_id === "string" && item.parent_id ? { parentId: item.parent_id } : {}),
    ...(typeof item.active_form === "string" && item.active_form
      ? { activeForm: item.active_form }
      : {}),
  };
}

// Maps the durable plan the store returns (harness `PlanItem`s over the wire,
// snake_case `active_form`/`parent_id`) into the same `PlanTask[]` the message
// fold produces, so the checklist renders identically whichever source feeds
// it. Reuses `toTask`, so status validation and the snake→camel renames stay
// single-sourced. Store items always carry an id; the positional fallback only
// guards a malformed row.
export function planItemsToTasks(items: readonly unknown[]): PlanTask[] {
  return items.map((item, index) => toTask(item, `#db:${index}`));
}

const STATUS_ICONS: Record<string, PlanTaskStatus> = {
  "[ ]": PlanTaskStatus.pending,
  "[~]": PlanTaskStatus.in_progress,
  "[x]": PlanTaskStatus.completed,
  "[-]": PlanTaskStatus.cancelled,
  "[!]": PlanTaskStatus.blocked,
};

// `read_plan` renders one of two forms, both parsed here — it replaces the
// whole fold, so each must reconstruct the parent hierarchy. It is the only
// tool result that carries step ids. Returns null if no id-bearing plan line is
// found.
//
// - `render_flat` ("Current plan:") — `N. [icon] [id] content` lines, with a
//   following "   (subtask of: <parent>)" line under a nested step.
// - `render_tree` ("Current plan (hierarchical view):"), emitted when the model
//   calls `read_plan(hierarchical=true)` — nesting is carried by two-space-per-
//   level indentation before the number, with no "(subtask of:)" lines, so a
//   step's parent is the nearest shallower step.
const READ_PLAN_LINE = /^( *)\d+\.\s+(\[[ ~x!-]\])\s+\[([^\]]+)\]\s+(.*)$/;
const SUBTASK_OF_LINE = /^\s*\(subtask of:\s*([^)]+)\)\s*$/;
const TREE_INDENT_WIDTH = 2; // `render_tree` indents each depth level by two spaces

function parseReadPlanResult(result: string | undefined): PlanTask[] | null {
  if (!result) return null;
  const tasks: PlanTask[] = [];
  // ancestorAtDepth[d] is the id of the step currently open at indent depth d,
  // so a deeper step's parent is ancestorAtDepth[d - 1] (tree form only).
  const ancestorAtDepth: string[] = [];
  for (const line of result.split("\n")) {
    const match = READ_PLAN_LINE.exec(line);
    if (match) {
      const [, indent, icon, id, content] = match;
      const depth = Math.floor(indent.length / TREE_INDENT_WIDTH);
      const task: PlanTask = {
        id,
        content: content.trim(),
        status: STATUS_ICONS[icon] ?? PlanTaskStatus.pending,
      };
      const parentId = depth > 0 ? ancestorAtDepth[depth - 1] : undefined;
      if (parentId) task.parentId = parentId;
      ancestorAtDepth[depth] = id;
      ancestorAtDepth.length = depth + 1; // drop any deeper stale ancestors
      tasks.push(task);
      continue;
    }
    // Flat form: attach a "(subtask of: <parent>)" annotation to the step just
    // parsed, so the snapshot keeps its hierarchy.
    const subtaskOf = SUBTASK_OF_LINE.exec(line);
    if (subtaskOf && tasks.length > 0) {
      tasks[tasks.length - 1].parentId = subtaskOf[1].trim();
    }
  }
  return tasks.length > 0 ? tasks : null;
}

// `add_task`'s result is `Added step '<content>' with id: <id>`; `add_subtask`'s
// is `Added subtask '<content>' with id: <id> (parent: <parent>)`. Capture the
// id in either — the trailing `(parent: …)`, when present, is not part of the id.
const ADDED_ID = /with id:\s*(\S+?)(?:\s*\(parent:[^)]*\))?\s*$/;

function parseAddedId(result: string | undefined): string | null {
  if (!result) return null;
  const match = ADDED_ID.exec(result.trim());
  return match ? match[1] : null;
}

const VALID_STATUSES: ReadonlySet<string> = new Set(Object.values(PlanTaskStatus));

function normalizeStatus(value: unknown): PlanTaskStatus | null {
  if (typeof value !== "string" || !VALID_STATUSES.has(value)) return null;
  return value as PlanTaskStatus;
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
